/**
 * Token-Erneuerung fuer die rohen fetch()-Helfer (authorizedFetch).
 *
 * Hintergrund (Code-Restliste 1.2, Sep 2026): Das Access-Token lebt 1 Tag.
 * Der axios-Client (services/api.js) erneuert bei 401 sauber, die aelteren
 * Fetch-Helfer in utils/api.js und utils/authorizedFetch.js hatten KEINE
 * 401-Behandlung. 18 Dateien nutzen sie (Profil-Setup, Profil speichern,
 * Admin-Panels) - nach 24 h lieferten die stumm 401, waehrend der Rest der
 * App funktionierte.
 *
 * Ablauf: 401 -> einmal POST /auth/refresh -> Request mit neuem Token
 * wiederholen -> bei erneutem 401 (oder gescheitertem Refresh) Auth-Daten
 * loeschen und das globale 'auth:error'-Event feuern (wie der axios-Client).
 */
import { clearAuthStorage } from './auth';
import { AURA_CLIENT_HEADERS } from './auraClient';

const getBackendBaseUrl = () => process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';

// Nur EIN Refresh gleichzeitig: parallele 401er teilen sich dasselbe Promise.
let inflightRefresh = null;

/** Login/Register/Refresh selbst duerfen keinen Refresh ausloesen (Endlosschleife). */
export const isAuthRoute = (url = '') => /\/auth\/(login|register|refresh)(\?|$)/.test(String(url));

/** Fehler nach Ablauf des Zeitlimits: kein Urteil ueber das Token, sondern wie ein Netzfehler. */
const refreshTimeoutError = () => Object.assign(
  new Error('Token-Refresh: Zeitlimit ueberschritten'),
  { name: 'TimeoutError' }
);

const withTimeout = (promise, timeoutMs, onTimeout) => {
  let timer;
  const limit = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (onTimeout) onTimeout();
      reject(refreshTimeoutError());
    }, timeoutMs);
  });
  return Promise.race([promise, limit]).finally(() => clearTimeout(timer));
};

/**
 * Holt ein neues Access-Token beim Backend und legt es in localStorage ab.
 *
 * `timeoutMs` begrenzt die Wartezeit. Pflicht, wo ein haengender Request den
 * App-Start blockiert (AuthContext.checkUserAuth). Das Zeitlimit haengt bewusst
 * nicht allein am AbortController: In der App laeuft fetch() ueber CapacitorHttp
 * (nativer HTTP-Stack), und der ignoriert das Abbruchsignal bei POST.
 *
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<string>} neues Token
 * @throws wenn kein Token vorhanden ist, der Refresh scheitert oder das
 *   Zeitlimit ablaeuft (dann mit name === 'TimeoutError')
 */
export const refreshAccessToken = async ({ timeoutMs } = {}) => {
  // Auch wer sich an einen laufenden Refresh ohne Zeitlimit anhaengt, wartet
  // mit Zeitlimit hoechstens timeoutMs.
  if (inflightRefresh) {
    return timeoutMs ? withTimeout(inflightRefresh, timeoutMs) : inflightRefresh;
  }

  const controller = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timedOut = false;

  const request = (async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Kein Token vorhanden');
    }

    const response = await fetch(`${getBackendBaseUrl()}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...AURA_CLIENT_HEADERS },
      body: JSON.stringify({ token }),
      ...(controller ? { signal: controller.signal } : {})
    });

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    if (!response.ok || !data?.token) {
      throw new Error(`Token-Refresh fehlgeschlagen (${response.status})`);
    }

    // Nach dem Zeitlimit gilt der Refresh als gescheitert. Eine spaete Antwort darf
    // kein Token mehr schreiben: Inzwischen kann sich ein anderes Konto angemeldet haben.
    if (timedOut) {
      throw refreshTimeoutError();
    }

    localStorage.setItem('token', data.token);
    localStorage.setItem('token_set_time', Date.now().toString());
    return data.token;
  })();

  // Das Zeitlimit beendet auch das geteilte Promise. Sonst hingen spaetere Aufrufer
  // an einem Refresh, auf dessen Antwort niemand mehr wartet.
  inflightRefresh = timeoutMs
    ? withTimeout(request, timeoutMs, () => {
      timedOut = true;
      if (controller) controller.abort();
    })
    : request;

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
};

/** Auth-Daten loeschen und App-weit "Sitzung abgelaufen" melden. */
export const handleSessionExpired = (url) => {
  clearAuthStorage();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('auth:error', {
      detail: {
        message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
        source: 'fetch',
        url
      }
    }));
  }
};

/**
 * Fuehrt einen Request aus und wiederholt ihn bei 401 genau einmal mit einem
 * frisch erneuerten Token.
 *
 * @param {(token: string) => Promise<Response>} doRequest - baut und sendet den Request mit dem uebergebenen Token
 * @param {string|null} initialToken - Token des ersten Versuchs
 * @param {string} url - nur fuer Logging/Event und den Auth-Routen-Check
 * @returns {Promise<Response>}
 */
export const fetchWithTokenRefresh = async (doRequest, initialToken, url) => {
  const response = await doRequest(initialToken);

  // Ohne Token gibt es nichts zu erneuern - und auf Auth-Routen nie retryen.
  if (response.status !== 401 || !initialToken || isAuthRoute(url)) {
    return response;
  }

  let newToken;
  try {
    newToken = await refreshAccessToken();
  } catch (refreshError) {
    console.warn('Token-Refresh fehlgeschlagen:', refreshError.message);
    handleSessionExpired(url);
    return response;
  }

  const retried = await doRequest(newToken);
  if (retried.status === 401) {
    handleSessionExpired(url);
  }
  return retried;
};
